import { ReactNode } from "react";
import {
  parseOrgPlanName,
  usePermissionQuery,
  useTellescopeSession,
  type SupportedSession,
  type TellescopeModelFromName,
} from "@tellescope/tanstack-query";
import { SetupDialogFlow } from "../../components";
import { deriveSetupFlow, type SetupNotification } from "../../setup-flow";

export interface SetupDialogProviderProps {
  children: ReactNode;
  selectedOrganization: TellescopeModelFromName<"organizations">;
  session?: SupportedSession;
  /** Surface a success notification from a setup step (e.g. a toast). */
  onSuccess?: (notification: SetupNotification) => void;
  /** Surface an error/warning notification from a setup step. */
  onError?: (notification: SetupNotification) => void;
}

export const SetupDialogProvider = ({
  children,
  selectedOrganization,
  session: providedSession,
  onSuccess,
  onError,
}: SetupDialogProviderProps) => {
  const session = useTellescopeSession(providedSession) ?? undefined;

  const {
    update: { access: canSetup },
  } = usePermissionQuery({
    model: "organizations",
    actions: ["update"],
    session,
  });

  // Don't surface the setup dialog until the user has cleared every auth flow
  // standing between login and the app itself (auth token issued + MFA satisfied);
  // until then userInfo is populated but the user can't yet reach the app.
  const requiresMFA = !!session?.userInfo && "requiresMFA" in session.userInfo && !!session.userInfo.requiresMFA;
  const hasCompletedAuthFlows = !!session?.authToken && !requiresMFA;

  // Whole-flow completion is derived from the steps (no persisted `completed`
  // flag), so the gate lives here — the provider can read the step registry that
  // `needsOnboarding` (a lower tier) cannot.
  const plan = parseOrgPlanName(selectedOrganization.plan?.type);
  const isFlowComplete = plan
    ? deriveSetupFlow({ plan, status: selectedOrganization.onboardingStatus ?? {} }).isFlowComplete
    : false;

  const needsSetup = hasCompletedAuthFlows && !!plan && !isFlowComplete && !!session.userInfo;

  return (
    <>
      {children}
      {needsSetup && canSetup ? <SetupDialogFlow onSuccess={onSuccess} onError={onError} /> : null}
    </>
  );
};
